/**
 * Paper Details dialog (round 11 rework). ONE reference page for EVERY paper
 * in the catalogue — no C-Type / fine-art context filtering and no Select
 * buttons: papers are picked with the normal controls, this dialog is a
 * glanceable reference opened from the "ⓘ Paper Details" chips (Configure,
 * editor, wizard and the Step-2 sticky bar). Each card: the real paper sample
 * photo (app/public/papers/{paperId}.webp, square, lazy-loaded), the name +
 * gsm, two short benefit-led bullets, and the pricing badge ("Best Seller").
 * Esc / ✕ close.
 *
 * CLICK-THROUGH (owner 2026-08-16): Clarity showed people tapping the cards
 * expecting them to DO something. With a `select` context (current size +
 * paper + a pick callback) cards valid for that size become tappable and
 * apply the paper; the current paper reads "Your pick ✓"; papers from the
 * OTHER family show a muted "C-Type/Fine art sizes only" caption and stay
 * inert — the dialog is never a door between families, so a fine-art
 * customer can't be walked back down to Gloss here (the tier chip remains
 * the only crossing). Without `select` (or before a size is picked, or while
 * a frame is pending) it stays the reference page it always was.
 */

import { parseSizeId, type PaperId, type PricingData } from '@rpl/pricing';
import { track } from '../../lib/analytics';
import { isPaperValidForSize } from '../../lib/sizePaper';
import { ModalShell } from './ModalShell';

/**
 * Two short, plain-language selling points per paper — display copy distilled
 * from the pricing descriptions (the papers themselves always come from
 * PricingData). A paper without an entry falls back to its pricing description.
 */
const PAPER_BULLETS: Partial<Record<PaperId, [string, string]>> = {
  gloss: ['Shiny with bold colour', 'Best for landscapes and street'],
  lustre: ['Soft pearl sheen, no glare', 'Best for portraits, albums and framing'],
  'bauhaus-matt': ['Smooth flat matt, no reflections', 'Great for bold images in bright rooms'],
  'bauhaus-lustre': ['The all-rounder with a fine texture', 'Shrugs off glare and fingerprints'],
  'bauhaus-metallic': ['High gloss with a pearly shimmer', 'Makes water, metal and city lights glow'],
  'ilford-pearl': ['Smooth satin, classic darkroom look', 'Top choice for black & white'],
  'ilford-cottonrag-smooth': ['Velvety matt, 100% cotton', 'The gallery pick for limited editions'],
  'ilford-cottonrag-textured': ['Textured matt, 100% cotton', 'Adds a handmade, watercolour feel'],
};

export interface PaperInfoDialogProps {
  pricing: PricingData;
  onClose: () => void;
  /** Click-through context — omit to keep the dialog reference-only. */
  select?: {
    sizeId: string | null;
    paper: PaperId;
    onPick: (paper: PaperId) => void;
  };
}

export function PaperInfoDialog({ pricing, onClose, select }: PaperInfoDialogProps) {
  const sizeId = select?.sizeId ?? null;
  const family = sizeId ? parseSizeId(sizeId).family : null;
  const ctPapers = new Set<PaperId>(pricing.productLines.ctype_standard.papers);
  return (
    <ModalShell onClose={onClose} width="min(720px, 94vw)" label="Paper details" className="pinfo">
      <div className="pinfo__head">
        <div>
          <div className="pinfo__title">Paper details</div>
          <div className="pinfo__sub">
            {sizeId
              ? 'Every paper we print on, side by side. Tap one to print on it.'
              : 'Every paper we print on, side by side. Pick yours with the paper control — this page is just for looking.'}
          </div>
        </div>
        <button type="button" className="pinfo__close" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </div>

      <div className="pinfo__cards">
        {(Object.keys(pricing.papers) as PaperId[]).map((paper) => {
          const info = pricing.papers[paper];
          if (!info) return null;
          const bullets = PAPER_BULLETS[paper];
          const isMine = !!sizeId && select!.paper === paper;
          const pickable = !!sizeId && !isMine && isPaperValidForSize(pricing, sizeId, paper);
          /* Family caption for inert cards — says WHY, not just "no". */
          const naNote =
            sizeId && !isMine && !pickable
              ? ctPapers.has(paper)
                ? 'C-Type sizes only'
                : family === 'fine_art'
                  ? 'Not in this size'
                  : 'Fine art sizes only'
              : null;
          const Card = pickable ? 'button' : 'div';
          return (
            <Card
              key={paper}
              {...(pickable
                ? {
                    type: 'button' as const,
                    onClick: () => {
                      track('paper_details_pick', { paper });
                      select!.onPick(paper);
                      onClose();
                    },
                  }
                : {})}
              className={`pinfo__card${pickable ? ' pinfo__card--pick' : ''}${isMine ? ' pinfo__card--mine' : ''}`}
            >
              <div className="pinfo__img" aria-hidden>
                <img
                  // BASE_URL-aware: '/' in dev/mock, '/apps/print/' on the live
                  // storefront (a root-relative /papers/… 404s through the proxy).
                  src={`${import.meta.env.VITE_ASSET_ORIGIN ?? ''}${import.meta.env.BASE_URL}papers/${paper}.webp`}
                  alt=""
                  loading="lazy"
                />
                {info.badge && <span className="pinfo__badge">{info.badge}</span>}
              </div>
              <div className="pinfo__body">
                <div className="pinfo__name">{info.label}</div>
                {info.gsm != null && <div className="pinfo__gsm">{info.gsm} gsm</div>}
                <ul className="pinfo__points">
                  {bullets ? bullets.map((b) => <li key={b}>{b}</li>) : <li>{info.description}</li>}
                </ul>
                {isMine && <span className="pinfo__mine">Your pick ✓</span>}
                {pickable && <span className="pinfo__pick">Print on this →</span>}
                {naNote && <span className="pinfo__na">{naNote}</span>}
              </div>
            </Card>
          );
        })}
      </div>
    </ModalShell>
  );
}
